import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CobaltFlagMessageService, CobaltFlagMessageVariant } from '@cae/cobalt-angular/components/flag-message';
import { Message } from '@core/models/message.model';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { EDocumentType } from '@precheckin/models/enums/document-type.enum';
import { VisitStatus } from '@precheckin/models/enums/visit-status.enum';
import { PageFile } from '@precheckin/models/visit-document/page-file.model';
import { VisitDocumentMetaData } from '@precheckin/models/visit-document/visit-document-metadata.model';
import { VisitDocumentPage } from '@precheckin/models/visit-document/visit-document-page.model';
import { IVisitDocument, VisitDocument } from '@precheckin/models/visit-document/visit-document.model';
import { HelperService } from '@precheckin/services/helper.service';
import { VisitDocumentPagesService } from '@precheckin/services/visit-document-pages.service';
import { VisitDocumentsService } from '@precheckin/services/visit-documents.service';
import { BannerService } from '@shared/components/banner/banner.service';
import { LoadingCircularPanelService } from '@shared/components/loading-circular-panel/loading-circular-panel.service';
import { concatMap, firstValueFrom, forkJoin, from, map, Observable, of, take, timer, toArray } from 'rxjs';
import { AppStoreKeys } from 'src/app/store/store-keys.module';

import { DeleteDocument, DeletePage, GetDocuments, InitializeDocuments, ResetDocuments, SetAllDocumentsPagesReady, SetIsDocumentReady, SetMedicalCertificates, SetPassport, SetPilotLicenses, SetVisa, UpSertDocument } from './precheckin-visit-document.actions';
import { deafultVisitDocumentState, IPrecheckinVisitDocumentStateModel } from './precheckin-visit-document.model';
import { SetHasRequestError, SetHasTriedToSave } from '../shared-state/precheckin-shared-state.actions';
import { GetVisit } from '../visit/precheckin-visit.actions';

@State<IPrecheckinVisitDocumentStateModel>({
  name: AppStoreKeys.PreCheckinVisitDocumentState,
  defaults: deafultVisitDocumentState
})
@Injectable()
export class PreCheckinVisitDocumentState {
  constructor(
    private _visitDocumentsService: VisitDocumentsService,
    private _store: Store,
    private _visitDocumentPagesService: VisitDocumentPagesService,
    private _sanitizer: DomSanitizer,
    private _cobaltFlagMessageService: CobaltFlagMessageService,
    private _loadingCircularPanelService: LoadingCircularPanelService,
    private _helperService: HelperService,
    private _bannerService: BannerService
  ) { }

  @Selector()
  public static isVisitDocutmentReady(
    state: IPrecheckinVisitDocumentStateModel
  ): boolean {
    return state.isVisitDocumentReady;
  }

  @Selector()
  public static getPassport(
    state: IPrecheckinVisitDocumentStateModel
  ): VisitDocument {
    return state.passport;
  }

  @Selector()
  public static getPilotLicenses(
    state: IPrecheckinVisitDocumentStateModel
  ): VisitDocument[] {
    return state.pilotLicenses;
  }

  @Selector()
  public static getMedicalCertificates(
    state: IPrecheckinVisitDocumentStateModel
  ): VisitDocument[] {
    return state.medicalCertificates;
  }

  @Selector()
  public static getVisa(
    state: IPrecheckinVisitDocumentStateModel
  ): VisitDocument {
    return state.visa;
  }

  @Selector()
  public static hasTriedToUploadPages(
    state: IPrecheckinVisitDocumentStateModel
  ): boolean {
    return state.hasTriedToUploadPages;
  }

  @Selector()
  public static isAllDocumentsPagesReady(
    state: IPrecheckinVisitDocumentStateModel
  ): boolean {
    return (
      state.isPassportPagesReady &&
      state.isLicensePagesReady &&
      state.isMedicalCertificatesPagesReady &&
      state.isVisaPagesReady
    );
  }

  @Action(SetAllDocumentsPagesReady)
  setAllDocumentsPagesReady(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>
  ): void {
    ctx.patchState({
      isPassportPagesReady: true,
      isLicensePagesReady: true,
      isMedicalCertificatesPagesReady: true,
      isVisaPagesReady: true
    });
  }

  @Action(GetDocuments)
  getDocumentsByVisitId(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { visitId }: GetDocuments
  ): void {
    ctx.patchState({
      isVisitDocumentReady: false
    });

    this._visitDocumentsService
      .getDocumentByVisitId(visitId)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this._store.dispatch(new InitializeDocuments(response));
        },
        error: () => {
          ctx.patchState({
            isVisitDocumentReady: true
          });
        }
      });
  }

  @Action(InitializeDocuments)
  async initializeDocuments(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { documents }: InitializeDocuments
  ): Promise<void> {
    if (documents.length) {
      this._setDocuments(documents);
    } else {
      ctx.patchState({
        passport: new VisitDocument(),
        pilotLicenses: new Array<VisitDocument>(),
        medicalCertificates: new Array<VisitDocument>(),
        visa: new VisitDocument(),
        isPassportPagesReady: true,
        isLicensePagesReady: true,
        isMedicalCertificatesPagesReady: true,
        isVisaPagesReady: true,
        isVisitDocumentReady: true
      });
    }
  }

  @Action(SetPassport)
  async setPassport(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { passport }: SetPassport
  ): Promise<void> {
    if (passport.pages.length && passport.pages.some((x) => x.blobId)) {
      this._getDocumentPages(passport, ctx);
    } else {
      ctx.patchState({
        passport: new VisitDocument(passport),
        isPassportPagesReady: true,
        isVisitDocumentReady: true
      });
    }
  }

  @Action(SetPilotLicenses)
  async setPilotLicenses(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { pilotLicenses }: SetPilotLicenses
  ): Promise<void> {
    if (pilotLicenses?.some((pilotLicense) => pilotLicense.pages?.length)) {
      this._getListDocumentsPages(pilotLicenses, ctx);
      true;
    } else {
      ctx.patchState({
        pilotLicenses: pilotLicenses.map(
          (pilotLicense) => new VisitDocument(pilotLicense)
        ),
        isLicensePagesReady: true,
        isVisitDocumentReady: true
      });
    }
  }

  @Action(SetMedicalCertificates)
  async setMedicalCertificates(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { medicalCertificates }: SetMedicalCertificates
  ): Promise<void> {
    if (
      medicalCertificates?.some(
        (medicalCertificate) => medicalCertificate.pages?.length
      )
    ) {
      this._getListDocumentsPages(medicalCertificates, ctx);
    } else {
      ctx.patchState({
        medicalCertificates: medicalCertificates?.map(
          (medicalCertificate) => new VisitDocument(medicalCertificate)
        ),
        isMedicalCertificatesPagesReady: true,
        isVisitDocumentReady: true
      });
    }
  }

  @Action(SetVisa)
  async setVisa(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { visa }: SetVisa
  ): Promise<void> {
    if (visa?.pages.length) {
      this._getDocumentPages(visa, ctx);
    } else {
      ctx.patchState({
        visa: new VisitDocument(visa),
        isVisaPagesReady: true,
        isVisitDocumentReady: true
      });
    }
  }

  processUpSertVisitDocuments(
    visitDocuments: VisitDocument[],
    _ctx: StateContext<IPrecheckinVisitDocumentStateModel>
  ): Observable<VisitDocument[]> {
    return from(visitDocuments).pipe(
      concatMap((document) => {
        const documentRequest = new VisitDocument(document);
        documentRequest.metaData = this._helperService.convertMetaDataToObject(
          document.metaData as Map<string, VisitDocumentMetaData>
        );
        documentRequest.pages = [];

        if (document.id) {
          documentRequest.pages = document.pages.filter((x) => !!x.blobId);
          const filesToUpload = document.pages.filter((x) => !x.blobId);

          return this._visitDocumentsService.put(documentRequest).pipe(
            concatMap((result: VisitDocument) => {
              if (!filesToUpload || filesToUpload.length === 0) {
                const newDocument = new VisitDocument(result);
                for (const pageNewDocument of newDocument.pages) {
                  const correspondingPageInDocument = document.pages.find(
                    (pageDocument) =>
                      pageDocument.blobId === pageNewDocument.blobId
                  );

                  if (correspondingPageInDocument) {
                    pageNewDocument.pageFile =
                      correspondingPageInDocument.pageFile;
                  }
                }

                return of(newDocument);
              }

              const newDocument = new VisitDocument(result);
              newDocument.pages = document.pages.map(
                (x) => new VisitDocumentPage(x)
              );

              const filesToUploadObservables = filesToUpload.map(
                (file, index) => {
                  return timer(index * 1000).pipe(
                    concatMap(() =>
                      this._visitDocumentPagesService.upload(
                        result.id,
                        '',
                        file.pageFile.file
                      )
                    )
                  );
                }
              );

              return forkJoin(filesToUploadObservables).pipe(
                map((fileUploadedResults) => {
                  const uploadedPages = newDocument.pages.filter(
                    (page) => !page.blobId
                  );

                  uploadedPages.forEach((page, index) => {
                    const response = fileUploadedResults[index];
                    page.blobId = response.blobId;
                    page.name = response.name;
                    page.type = response.type;
                  });

                  return newDocument;
                })
              );
            })
          );
        } else {
          return this._visitDocumentsService.post(documentRequest).pipe(
            concatMap((result: VisitDocument) => {
              if (!document.pages || document.pages.length === 0) {
                document = new VisitDocument(result);
                return of(document);
              }

              const filesToUpload = document.pages.map(
                (x) => new VisitDocumentPage(x)
              );

              document = new VisitDocument(result);
              document.pages = filesToUpload;

              const filesToUploadObservables = filesToUpload.map(
                (file, index) => {
                  return timer(index * 1000).pipe(
                    concatMap(() =>
                      this._visitDocumentPagesService.upload(
                        result.id,
                        '',
                        file.pageFile.file
                      )
                    )
                  );
                }
              );

              return forkJoin(filesToUploadObservables).pipe(
                map((fileUploadedResults) => {
                  const newDocument = new VisitDocument(document);
                  newDocument.pages.map((x, index) => {
                    x.blobId = fileUploadedResults[index].blobId;
                    x.name = fileUploadedResults[index].name;
                    x.type = fileUploadedResults[index].type;
                    return x;
                  });
                  return newDocument;
                })
              );
            })
          );
        }
      }),
      toArray()
    );
  }

  processUpSertVisitDocument(
    document: VisitDocument,
    _ctx: StateContext<IPrecheckinVisitDocumentStateModel>
  ): Observable<VisitDocument> {
    const documentRequest = new VisitDocument(document);
    documentRequest.metaData = this._helperService.convertMetaDataToObject(
      document.metaData as Map<string, VisitDocumentMetaData>
    );
    documentRequest.pages = [];

    if (document.id) {
      documentRequest.pages = document.pages.filter((x) => !!x.blobId);
      const filesToUpload = document.pages.filter((x) => !x.blobId);

      return this._visitDocumentsService.put(documentRequest).pipe(
        concatMap((result: VisitDocument) => {
          if (!filesToUpload || filesToUpload.length === 0) {
            const response = new VisitDocument(result);
            response.pages = document.pages.map(
              (x) => new VisitDocumentPage(x)
            );
            return of(response);
          }

          const updatedDocument = new VisitDocument(result);
          updatedDocument.pages = document.pages.map(
            (x) => new VisitDocumentPage(x)
          );

          const filesToUploadObservables = filesToUpload.map((file, index) => {
            return timer(index * 1000).pipe(
              concatMap(() =>
                this._visitDocumentPagesService.upload(
                  result.id,
                  '',
                  file.pageFile.file
                )
              )
            );
          });

          return forkJoin(filesToUploadObservables).pipe(
            map((fileUploadedResults) => {
              const uploadedPages = updatedDocument.pages.filter(
                (page) => !page.blobId
              );

              uploadedPages.forEach((page, index) => {
                const response = fileUploadedResults[index];
                page.blobId = response.blobId;
                page.name = response.name;
              });

              return updatedDocument;
            })
          );
        })
      );
    } else {
      return this._visitDocumentsService.post(documentRequest).pipe(
        concatMap((result: VisitDocument) => {
          if (!document.pages || document.pages.length === 0) {
            document = new VisitDocument(result);
            return of(document);
          }

          const filesToUpload = document.pages.map(
            (x) => new VisitDocumentPage(x)
          );

          document = new VisitDocument(result);
          document.pages = filesToUpload;

          const filesToUploadObservables = filesToUpload.map((file, index) => {
            return timer(index * 1000).pipe(
              concatMap(() =>
                this._visitDocumentPagesService.upload(
                  result.id,
                  '',
                  file.pageFile.file
                )
              )
            );
          });

          return forkJoin(filesToUploadObservables).pipe(
            map((fileUploadedResults) => {
              const newDocument = new VisitDocument(document);
              newDocument.pages.map((x, index) => {
                x.blobId = fileUploadedResults[index].blobId;
                x.name = fileUploadedResults[index].name;
                x.type = fileUploadedResults[index].type;
                return x;
              });
              return newDocument;
            })
          );
        })
      );
    }
  }

  @Action(UpSertDocument)
  async upSertDocument(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { documents, visitStatus }: UpSertDocument
  ): Promise<void> {
    this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
      true
    );

    this._store.dispatch(new SetHasTriedToSave(false));

    try {
      let finalResults: VisitDocument | VisitDocument[] = [];

      if (Array.isArray(documents)) {
        finalResults = await firstValueFrom(
          this.processUpSertVisitDocuments(documents, ctx)
        );
      } else {
        finalResults = await firstValueFrom(
          this.processUpSertVisitDocument(documents, ctx)
        );
      }

      this._setDocumentInContext(ctx, finalResults);

      if (visitStatus === VisitStatus.PRECHECKED_IN) {
        if (Array.isArray(documents)) this._store.dispatch(new GetVisit(documents[0].visitId))
        else this._store.dispatch(new GetVisit(documents.visitId));
      }

      this._store.dispatch(new SetHasTriedToSave(true));
      this._store.dispatch(new SetHasRequestError(false));
    } catch (error) {
      this._setDocumentInContext(ctx, documents);

      this._cobaltFlagMessageService.showMessage({
        message: 'Document not created.',
        variant: CobaltFlagMessageVariant.Error
      });

      this._store.dispatch(new SetHasRequestError(true));
      this._store.dispatch(new SetHasTriedToSave(true));
    } finally {
      this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
        false
      );
    }
  }

  @Action(DeleteDocument)
  async deleteDocument(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { document }: DeleteDocument
  ): Promise<void> {
    this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
      true
    );

    this._visitDocumentsService
      .delete(document)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._deleteDocumentFromContext(ctx, document);

          this._cobaltFlagMessageService.showMessage({
            message: 'Document deleted successfully',
            variant: CobaltFlagMessageVariant.Success
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        },
        error: () => {
          this._setDocumentInContext(ctx, document);

          this._cobaltFlagMessageService.showMessage({
            message: 'Document not deleted.',
            variant: CobaltFlagMessageVariant.Error
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        }
      });
  }

  @Action(DeletePage)
  async deletePage(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { document, blobId }: DeletePage
  ): Promise<void> {
    const newDocument = new VisitDocument(document);

    this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
      true
    );

    this._visitDocumentPagesService
      .delete(document.id, blobId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          newDocument.pages = document.pages.filter((element) => {
            return !!element.blobId && element.blobId !== blobId;
          });

          this._setDocumentInContext(ctx, newDocument);

          this._cobaltFlagMessageService.showMessage({
            message: 'File deleted successfully',
            variant: CobaltFlagMessageVariant.Success
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        },
        error: () => {
          this._setDocumentInContext(ctx, newDocument);

          this._cobaltFlagMessageService.showMessage({
            message: 'File not deleted.',
            variant: CobaltFlagMessageVariant.Error
          });

          this._loadingCircularPanelService.toggleLoadingCircularPanelVisibility(
            false
          );
        }
      });
  }

  @Action(ResetDocuments)
  resetDocuments(ctx: StateContext<IPrecheckinVisitDocumentStateModel>): void {
    ctx.patchState(deafultVisitDocumentState);
  }

  @Action(SetIsDocumentReady)
  setIsDocumentReady(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    { isReady }: SetIsDocumentReady
  ): void {
    ctx.patchState({
      isVisitDocumentReady: isReady
    });
  }

  private _getListDocumentsPages(
    documents: IVisitDocument[],
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>
  ): void {
    const observables: Observable<Blob[]>[] = [];
    const documentsWithPages = documents.filter((x) => x.pages.length);
    const documentsWithoutPages = documents.filter((x) => !x.pages.length);

    for (
      let licenseIndex = 0;
      licenseIndex < documentsWithPages.length;
      licenseIndex++
    ) {
      const license = documentsWithPages[licenseIndex];
      const licenseObservables: Observable<Blob>[] = [];

      for (let pageIndex = 0; pageIndex < license.pages.length; pageIndex++) {
        const page = license.pages[pageIndex];

        const pageObservable = this._visitDocumentPagesService
          .download(license.id, page.blobId)
          .pipe(map((res: Blob) => res));

        licenseObservables.push(pageObservable);
      }
      observables.push(forkJoin(licenseObservables));
    }

    forkJoin(observables).subscribe({
      next: (responses) => {
        for (
          let responseIndex = 0;
          responseIndex < responses.length;
          responseIndex++
        ) {
          const licenseResponse = responses[responseIndex];

          for (
            let pageIndex = 0;
            pageIndex < licenseResponse.length;
            pageIndex++
          ) {
            const res = licenseResponse[pageIndex];
            const url = URL.createObjectURL(res);

            const pageFile = new PageFile({
              file: new File(
                [res],
                documentsWithPages[responseIndex].pages[pageIndex].name,
                { type: res.type }
              ),
              url: this._sanitizer.bypassSecurityTrustUrl(url)
            });

            documentsWithPages[responseIndex].pages[pageIndex].pageFile =
              pageFile;
          }
        }

        this._updateStateListDocuments(
          documents[0].type,
          documentsWithPages as VisitDocument[],
          documentsWithoutPages as VisitDocument[],
          ctx,
          false
        );
      },
      error: () => {
        this._updateStateListDocuments(
          documents[0].type,
          documentsWithPages as VisitDocument[],
          documentsWithoutPages as VisitDocument[],
          ctx,
          true
        );
      }
    });
  }

  private _updateStateListDocuments(
    documentType: EDocumentType,
    documentsWithPages: VisitDocument[],
    documentsWithoutPages: VisitDocument[],
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    hasError: boolean
  ): void {
    if (documentType === EDocumentType.PILOT_CERTIFICATE) {
      ctx.patchState({
        pilotLicenses: [
          ...documentsWithPages.map((x) => new VisitDocument(x)),
          ...documentsWithoutPages.map((x) => new VisitDocument(x))
        ],
        isVisitDocumentReady: true,
        isLicensePagesReady: true
      });
    } else {
      ctx.patchState({
        medicalCertificates: [
          ...documentsWithPages.map((x) => new VisitDocument(x)),
          ...documentsWithoutPages.map((x) => new VisitDocument(x))
        ],
        isVisitDocumentReady: true,
        isMedicalCertificatesPagesReady: true
      });
    }

    if (hasError) {
      this._bannerService.showMessage(
        Message.create(
          'An error occurred while downloading pages. Please try again in a few minutes.',
          'critical'
        )
      );
    }
  }

  private _getDocumentPages(
    document: IVisitDocument,
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>
  ): void {
    const requests = new Array<Observable<Blob>>();
    const newDocumet = new VisitDocument(document);

    for (let index = 0; index < document.pages.length; index++) {
      if (document.pages[index].blobId) {
        requests[index] = this._visitDocumentPagesService
          .download(document.id, document.pages[index].blobId)
          .pipe(map((res: Blob) => res));
      }
    }

    forkJoin(requests).subscribe({
      next: (response) => {
        for (let index = 0; index < response.length; index++) {
          const url = URL.createObjectURL(response[index]);

          const pageFile = new PageFile({
            file: new File([response[index]], document.pages[index].name, {
              type: response[index].type
            }),
            url: this._sanitizer.bypassSecurityTrustUrl(url)
          });

          newDocumet.pages[index].pageFile = pageFile;
        }

        this._updateStateDocument(newDocumet, ctx, false);
      },
      error: () => {
        this._updateStateDocument(newDocumet, ctx, true);
      }
    });
  }

  private _updateStateDocument(
    newDocumet: VisitDocument,
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    hasError: boolean
  ): void {
    if (newDocumet.type === EDocumentType.VISA) {
      ctx.patchState({
        visa: newDocumet,
        isVisitDocumentReady: true,
        isVisaPagesReady: true
      });
    } else {
      ctx.patchState({
        passport: newDocumet,
        isVisitDocumentReady: true,
        isPassportPagesReady: true
      });
    }

    if (hasError) {
      this._bannerService.showMessage(
        Message.create(
          'An error occurred while downloading pages. Please try again in a few minutes.',
          'critical'
        )
      );
    }
  }

  private async _setDocuments(visitDocuments: IVisitDocument[]): Promise<void> {
    const passport = visitDocuments.find(
      (document) => document.type === EDocumentType.PASSPORT
    );
    const pilotLicenses = visitDocuments.filter(
      (document) => document.type === EDocumentType.PILOT_CERTIFICATE
    );
    const medicalCertificates = visitDocuments.filter(
      (document) => document.type === EDocumentType.MEDICAL_CERTIFICATE
    );
    const visa = visitDocuments.find(
      (document) => document.type === EDocumentType.VISA
    );

    this._store.dispatch(new SetPassport(passport));

    this._store.dispatch(new SetPilotLicenses(pilotLicenses));

    this._store.dispatch(new SetMedicalCertificates(medicalCertificates));

    this._store.dispatch(new SetVisa(visa));
  }

  private _setDocumentInContext(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    documents: VisitDocument | VisitDocument[]
  ): void {
    if (Array.isArray(documents)) {
      switch (documents[0].type) {
        case EDocumentType.PILOT_CERTIFICATE:
          ctx.patchState({
            pilotLicenses: documents.map((x) => new VisitDocument(x)),
            hasTriedToUploadPages: true
          });
          break;
        case EDocumentType.MEDICAL_CERTIFICATE:
          ctx.patchState({
            medicalCertificates: documents.map((x) => new VisitDocument(x)),
            hasTriedToUploadPages: true
          });
          break;
      }
    } else {
      switch (documents.type) {
        case EDocumentType.PASSPORT:
          ctx.patchState({
            passport: new VisitDocument(documents),
            hasTriedToUploadPages: true
          });
          break;
        case EDocumentType.VISA:
          ctx.patchState({
            visa: new VisitDocument(documents),
            hasTriedToUploadPages: true
          });
          break;
      }
    }
  }

  private _deleteDocumentFromContext(
    ctx: StateContext<IPrecheckinVisitDocumentStateModel>,
    document: VisitDocument
  ): void {
    const ctxState = ctx.getState();

    switch (document.type) {
      case EDocumentType.PILOT_CERTIFICATE:
        ctx.patchState({
          pilotLicenses: ctxState.pilotLicenses.filter((x) => x.id !== document.id).map((x) => new VisitDocument(x)),
          hasTriedToUploadPages: true
        });
        break;
      case EDocumentType.MEDICAL_CERTIFICATE:
        ctx.patchState({
          medicalCertificates: ctxState.medicalCertificates.filter((x) => x.id !== document.id).map(
            (x) => new VisitDocument(x)
          ),
          hasTriedToUploadPages: true
        });
        break;
    }
  }
}
